/**
 * CRO slider
 *
 * The CRO blocks are a plain CSS grid on desktop and a scroll-snap slider on
 * phones (see _cro-base.scss). This module only decorates that slider with
 * arrows and dots; without JavaScript it still swipes.
 *
 * Mark up a track with <div class="cro-slider" data-cro-slider> containing
 * .cro-slider__slide children.
 */

const PHONE_QUERY = '(max-width: 767px)';

function activeIndex(track) {
    const center = track.scrollLeft + track.clientWidth / 2;
    let closest = 0;
    let distance = Infinity;

    Array.from(track.children).forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
        const delta = Math.abs(slideCenter - center);

        if (delta < distance) {
            distance = delta;
            closest = index;
        }
    });

    return closest;
}

// A mandatory snap container can cancel a programmatic smooth scroll, so
// snapping is suspended for the duration of the slide change.
function animate(track, left) {
    if (Math.abs(left - track.scrollLeft) < 1) {
        return;
    }

    const restore = () => {
        track.style.scrollSnapType = '';
    };

    track.style.scrollSnapType = 'none';
    track.scrollTo({ left, behavior: 'smooth' });

    if ('onscrollend' in track) {
        track.addEventListener('scrollend', restore, { once: true });
    }

    window.setTimeout(restore, 700);
}

function scrollTo(track, index) {
    const slide = track.children[Math.max(0, Math.min(track.children.length - 1, index))];

    if (!slide) {
        return;
    }

    animate(track, slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2);
}

function build(track) {
    const slides = track.children.length;

    if (slides < 2 || track.parentNode === null) {
        return;
    }

    const holder = track.parentNode;
    const media = window.matchMedia(PHONE_QUERY);

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'cro-slider-arrow cro-slider-arrow--prev';
    prev.setAttribute('aria-label', 'Vorige');
    prev.innerHTML = '&lsaquo;';

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'cro-slider-arrow cro-slider-arrow--next';
    next.setAttribute('aria-label', 'Volgende');
    next.innerHTML = '&rsaquo;';

    const nav = document.createElement('div');
    nav.className = 'cro-slider-nav';

    const dots = [];

    const sync = (forced) => {
        const index = forced === undefined ? activeIndex(track) : forced;

        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        prev.disabled = index === 0;
        next.disabled = index === slides - 1;
    };

    for (let i = 0; i < slides; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cro-slider-nav__dot';
        dot.setAttribute('aria-label', `Ga naar ${i + 1}`);
        dot.addEventListener('click', () => {
            scrollTo(track, i);
            sync(i);
        });
        nav.appendChild(dot);
        dots.push(dot);
    }

    const step = (direction) => {
        const index = Math.max(0, Math.min(slides - 1, activeIndex(track) + direction));

        scrollTo(track, index);
        // Reflect the new slide right away rather than waiting for the scroll
        // events the smooth scroll will emit.
        sync(index);
    };

    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));

    // The arrows are absolutely positioned against the section, which already
    // is position: relative through _cro-base.scss.
    holder.appendChild(prev);
    holder.appendChild(next);
    track.insertAdjacentElement('afterend', nav);

    // Line the arrows up with the middle of the slide artwork, the way the
    // design has them, instead of the middle of the whole card.
    const positionArrows = () => {
        const first = track.children[0];

        if (!first) {
            return;
        }

        const art = first.querySelector('img') || first;
        const height = art.clientHeight || first.clientHeight;
        const top = track.offsetTop + height / 2 - 20;

        prev.style.top = `${top}px`;
        next.style.top = `${top}px`;
    };

    let pending = null;

    track.addEventListener('scroll', () => {
        if (pending) {
            return;
        }

        pending = window.setTimeout(() => {
            pending = null;
            sync();
        }, 80);
    }, { passive: true });

    const toggle = () => {
        // Arrows and dots are hidden by CSS above the phone breakpoint; keep
        // them out of the accessibility tree there as well.
        const on = media.matches;

        [prev, next, nav].forEach((el) => {
            el.hidden = !on;
        });

        if (on) {
            positionArrows();
            sync();
        }
    };

    if (media.addEventListener) {
        media.addEventListener('change', toggle);
    } else if (media.addListener) {
        media.addListener(toggle);
    }

    window.addEventListener('resize', toggle);
    window.addEventListener('load', toggle);

    toggle();
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-cro-slider]').forEach(build);
});
